import { useEffect, useState } from 'react';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ТАБЛИЦА БЕРЁТ ЦЕЛОЕ ОКНО, А СТРАНИЦА ДО НЕЁ ПРОКРУЧИВАЕТСЯ.
 *
 * 01.09.2026 владелец о списке кампаний: «мне кажется, там ещё
 * 1 кампания точно влезет». Через заход — о том, что вышло:
 * «стало наоборот меньше, а не больше, 2 кампании еле отображаются».
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ДВЕ НЕВЕРНЫЕ РЕДАКЦИИ, И ОБЕ ПОУЧИТЕЛЬНЫ.
 *
 * БЫЛО: `max-height: calc(100vh - 232px)` — число из эталона, где над
 * таблицей ровно та шапка, что нарисована. У нас над ней ещё и плитки
 * сводки. Число сходилось с местом таблицы случайно — отсюда и полоса
 * пустоты, которую владелец увидел.
 *
 * СТАЛО (пакет 63): «мерить, докуда от верха таблицы до низа окна».
 * Звучит точнее некуда — и на живой странице оставило две строки.
 *
 * ПОЧЕМУ. Подогнав таблицу ровно под низ окна, мы сделали страницу
 * РОВНО В ОКНО ВЫСОТОЙ. Прокручивать стало нечего — а значит, плитки
 * сводки над таблицей больше не убрать, и таблица заперта в остатке
 * под ними. Замер стенда: страница 868 при окне 880, прокрутка
 * «0 из 0», таблице 380 точек, три строки.
 *
 * Замер был верен и был ловушкой: он гасил единственный способ отдать
 * таблице место — прокрутку.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ЧТО ВЕРНО.
 *
 * Таблице даётся ЦЕЛОЕ ОКНО, и она этим делает страницу длиннее окна.
 * Человек прокручивает, плитки уходят вверх, таблица занимает экран
 * целиком. Внизу страницы её низ приходится ровно на низ окна — пустой
 * полосы не остаётся ни при какой шапке, потому что шапка к этому мигу
 * уже уехала.
 *
 * Замер стенда на том же наборе: семь строк вместо трёх, зазор под
 * таблицей в конце прокрутки — ноль.
 *
 * ПОЧЕМУ ЭТО НЕ ПРОСТО «ЧИСЛО ПОБОЛЬШЕ». Прежнее число зависело от
 * того, что стоит НАД таблицей, и устаревало при каждой правке шапки.
 * Это — не зависит ни от чего, кроме окна: над таблицей может появиться
 * что угодно, она всё равно займёт экран, как только до неё долистают.
 * ═══════════════════════════════════════════════════════════════════════
 */

/**
 * Что остаётся под таблицей, когда до неё долистали.
 *
 * НОЛЬ. Всякая точка здесь — это точка, которой не хватило строке,
 * и полоса пустоты внизу, из-за которой всё и началось. Воздух под
 * таблицей даёт поле страницы, а не эта высота.
 */
export const ПОДВАЛ_ТАБЛИЦЫ = 0;

/**
 * Сколько высоты берёт таблица.
 *
 * @param окно    высота окна (`innerHeight`)
 * @param подвал  что оставить под таблицей
 */
export function высотаТаблицы(окно: number, подвал: number = ПОДВАЛ_ТАБЛИЦЫ): number {
  /**
   * ПОЛ, А НЕ ГОЛАЯ РАЗНОСТЬ.
   *
   * На очень коротком окне разность уходит в ноль и ниже. Таблица
   * высотой в ноль — это не «мало места», это пропавший экран: человек
   * видит шапку столбцов и пустоту под ней и решает, что кампаний нет.
   */
  const ПОЛ = 200;
  return Math.max(ПОЛ, Math.round(окно - подвал));
}

/**
 * Округление замера.
 *
 * ЗАМЕР ДРОБНЫЙ, А ВЫСОТА В СОСТОЯНИИ. Браузер отдаёт высоту окна
 * дробной при увеличенном масштабе, и число меняется в третьем знаке
 * при каждом чихе раскладки — то есть перерисовывает таблицу без всякой
 * причины. Шаг в восемь точек глазом не виден, а дрожь снимает.
 */
export const ШАГ_ВЫСОТЫ = 8;

export function приниматьВысоту(было: number | null, стало: number): boolean {
  if (было === null) return true;
  return Math.abs(было - стало) >= ШАГ_ВЫСОТЫ;
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ОДНО МЕСТО НА ВСЕ ТАБЛИЦЫ.
 *
 * Их две — список кампаний и запросы кампании, — и правило у них одно.
 * Написанное дважды, оно разойдётся при первой же правке.
 *
 * ПОЧЕМУ ХУК, А НЕ `calc(100vh)` В ЧАСТИЦЕ. `100vh` на телефонах
 * считается от окна БЕЗ адресной строки, и таблица вылезает за экран
 * ровно на её высоту; `100dvh` умеют не все, до кого мы доезжаем.
 * `innerHeight` отдаёт то, что видно на самом деле, и меняется вместе
 * с адресной строкой.
 * ═══════════════════════════════════════════════════════════════════════
 */
/** Initial browser height needs no DOM measurement or post-mount render. */
export function initialTableHeight(viewportHeight: number | null, footer = ПОДВАЛ_ТАБЛИЦЫ): number | null {
  return viewportHeight === null || !Number.isFinite(viewportHeight) ? null : высотаТаблицы(viewportHeight, footer);
}

export function useВысотаДоНиза(подвал: number = ПОДВАЛ_ТАБЛИЦЫ): number | null {
  const [высота, setВысота] = useState<number | null>(() =>
    initialTableHeight(typeof window === 'undefined' ? null : window.innerHeight, подвал),
  );

  useEffect(() => {
    const мерить = () => {
      const h = высотаТаблицы(window.innerHeight, подвал);
      setВысота((было) => (приниматьВысоту(было, h) ? h : было));
    };
    мерить();
    /* ПРОКРУТКА ЗДЕСЬ НЕ НУЖНА, и это главное отличие от прошлой
       редакции: высота от неё не зависит вовсе, а зависимость от неё
       и была той ловушкой, что заперла таблицу под плитками. */
    window.addEventListener('resize', мерить);
    return () => window.removeEventListener('resize', мерить);
  }, [подвал]);

  return высота;
}
